/* Government renewal guides (contracts/gov-renewals.json) + document-kind mapping helpers.
 * Pure data/functions — no React — so tests and other components can reuse them. */
import RAW from "../../../../../contracts/gov-renewals.json";

export type Json = string | number | boolean | null | Json[] | { [k: string]: Json };

export interface RenewalGuideData {
  kind: string;
  who: "employer" | "employee" | string;
  platform: string;
  url: string;
  altUrls?: string[];
  leadTimeDays: number;
  leadTimeNote?: string;
  prerequisites: string[];
  steps: string[];
  stepsAr: string[];
  fees: { [k: string]: Json };
  penalties: { [k: string]: Json };
  tips: string[];
  tipsAr?: string[];
  sources: string[];
  verify: boolean;
  verifyNotes?: string | null;
}

export interface RenewalsFile { researchedOn: string; currency: string; disclaimer: string; documents: RenewalGuideData[] }

export const RENEWALS = RAW as unknown as RenewalsFile;
export const GUIDES: RenewalGuideData[] = RENEWALS.documents;
export const GUIDE_KINDS: string[] = GUIDES.map((g) => g.kind);

/** Arabic names of the guide kinds (and the common app document kinds). */
export const KIND_AR: Record<string, string> = {
  Iqama: "الإقامة",
  "Work permit": "رخصة العمل",
  Passport: "جواز السفر",
  "Exit re-entry visa": "تأشيرة الخروج والعودة",
  "Final exit visa": "تأشيرة الخروج النهائي",
  "Medical insurance": "التأمين الطبي",
  "National ID": "الهوية الوطنية",
  "Qiwa contract": "عقد العمل في قوى",
  "GOSI registration": "التسجيل في التأمينات الاجتماعية",
  "Driving licence": "رخصة القيادة",
  "Professional licence": "الاعتماد المهني",
};

/** Arabic platform names for the guide header (source JSON is English only). */
export const PLATFORM_AR: Record<string, string> = {
  Iqama: "منصة مقيم (عِلم) أو أبشر أعمال؛ تُسدَّد الرسوم عبر سداد",
  "Work permit": "منصة قوى (وزارة الموارد البشرية / تكامل)",
  Passport: "سفارة الموظف أو قنصليته (غالبًا عبر مراكز مثل VFS وBLS)، ثم يحدّث صاحب العمل بيانات الجواز في مقيم أو أبشر أعمال",
  "Exit re-entry visa": "مقيم أو أبشر أعمال (يصدرها صاحب العمل)، وأبشر أفراد للمرافقين",
  "Final exit visa": "مقيم أو أبشر أعمال (صاحب العمل)، أو أبشر أفراد للعامل بعد انتهاء العقد",
  "Medical insurance": "شركة تأمين مرخصة من مجلس الضمان الصحي (بوبا، التعاونية، ميدغلف…) والامتثال مرتبط بمقيم وأبشر",
  "National ID": "أبشر أفراد (الأحوال المدنية)",
  "Qiwa contract": "منصة قوى (خدمة إدارة العقود)",
  "GOSI registration": "التأمينات أعمال (gosi.gov.sa)",
  "Driving licence": "أبشر أفراد (الإدارة العامة للمرور)",
  "Professional licence": "الهيئة السعودية للتخصصات الصحية (ممارس بلس) أو الهيئة السعودية للمهندسين أو الجهة المختصة بالمهنة",
};

const norm = (s: string) => String(s || "").toLowerCase().replace(/[\s_\-–—/]+/g, " ").replace(/[()]/g, " ").replace(/\s+/g, " ").trim();

/** Keyword rules, most specific first. */
const RULES: [RegExp, string][] = [
  [/final exit|خروج نهائي|الخروج النهائي/, "Final exit visa"],
  [/re ?entry|reentry|exit and return|خروج وعودة|الخروج والعودة/, "Exit re-entry visa"],
  [/work permit|labou?r permit|رخصة العمل|رخصة عمل/, "Work permit"],
  [/iqama|residen(ce|cy)|muqeem|اقامة|إقامة|الإقامة/, "Iqama"],
  [/passport|جواز/, "Passport"],
  [/gosi|social insurance|التأمينات/, "GOSI registration"],
  [/medical|health insurance|insurance|cchi|\bchi\b|تأمين طبي|التأمين الطبي/, "Medical insurance"],
  [/national id|hawiya|hawiyah|\bnid\b|id card|الهوية الوطنية|هوية وطنية/, "National ID"],
  [/qiwa|employment contract|labou?r contract|contract|عقد/, "Qiwa contract"],
  [/driv|رخصة القيادة|رخصة قيادة/, "Driving licence"],
  [/professional|scfhs|mumaris|\bsce\b|engineer|accreditation|license|licence|اعتماد مهني|ترخيص مهني/, "Professional licence"],
];

/** Map an app document kind (e.g. "Exit re-entry visa (single)", "Iqama renewal") to a guide kind, or null. */
export function guideKindFor(docKind: string | null | undefined): string | null {
  const n = norm(docKind || "");
  if (!n) return null;
  const exact = GUIDE_KINDS.find((k) => norm(k) === n);
  if (exact) return exact;
  const prefix = GUIDE_KINDS.find((k) => n.startsWith(norm(k)));
  if (prefix) return prefix;
  for (const [re, kind] of RULES) if (re.test(n)) return kind;
  return null;
}

export function guideFor(docKind: string | null | undefined): RenewalGuideData | null {
  const k = guideKindFor(docKind);
  return k ? GUIDES.find((g) => g.kind === k) ?? null : null;
}

/** Remove a leading "1. " / "١. " numbering from a step (the UI numbers steps itself). */
export function stripNum(s: string): string {
  return String(s).replace(/^\s*[0-9٠-٩]+\s*[.)\-–]\s*/, "");
}

/** Arabic labels for fee/penalty keys (fallback: humanized camelCase). */
const KEY_AR: Record<string, string> = {
  iqamaFeeSARPerYear: "رسوم الإقامة (ر.س / سنة)",
  iqamaFeeDomesticWorkerSARPerYear: "رسوم إقامة العمالة المنزلية (ر.س / سنة)",
  periodsMonths: "مدد التجديد (بالأشهر)",
  proRata: "الاحتساب النسبي",
  workPermitFeeSARPerYear: "رسوم رخصة العمل (ر.س / سنة)",
  expatLevySARPerMonth: "المقابل المالي للوافد (ر.س / شهر)",
  expatsExceedSaudis: "إذا زاد الوافدون على السعوديين",
  expatsEqualOrFewerThanSaudis: "إذا تساوى الوافدون مع السعوديين أو قلّوا",
  dependentLevySARPerMonth: "المقابل المالي للمرافق (ر.س / شهر)",
  exemptions: "الإعفاءات",
  note: "ملاحظة",
  minimumPeriodMonths: "الحد الأدنى للمدة (بالأشهر)",
  single: "مفردة",
  multiple: "متعددة",
  baseSAR: "الرسم الأساسي (ر.س)",
  baseMonths: "أشهر الرسم الأساسي",
  extraMonthSAR: "كل شهر إضافي (ر.س)",
  extraMonthWhileAbroadSAR: "كل شهر إضافي خارج المملكة (ر.س)",
  refundable: "قابلة للاسترداد",
  visaFeeSAR: "رسوم التأشيرة (ر.س)",
  validityDays: "مدة الصلاحية (بالأيام)",
  renewalFeeSAR: "رسوم التجديد (ر.س)",
  feeSAR: "الرسوم (ر.س)",
  nonSaudi: "غير السعودي",
  saudiLegacy: "السعودي (النظام السابق)",
  saudiNewEntrantsSinceJul2024: "السعوديون المسجلون منذ يوليو 2024",
  wageCapSAR: "الحد الأعلى للأجر (ر.س)",
  privateLicenceSARPerYear: "رخصة خاصة (ر.س / سنة)",
  examples: "أمثلة",
  lateRenewal: "غرامة التأخر في التجديد",
  offence: "المخالفة",
  fineSAR: "الغرامة (ر.س)",
  extra: "إضافة",
  notReturning: "عدم العودة",
  notLeaving: "عدم المغادرة",
  validityRule: "قاعدة الصلاحية",
  employerNonCompliance: "عدم التزام صاحب العمل",
  operational: "الأثر التشغيلي",
  lateRenewalSAR: "غرامة التأخر (ر.س)",
  verify: "تحتاج تحققًا",
};

export function humanKey(k: string): string {
  const s = k
    .replace(/SAR/g, " SAR ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/(\d+)([a-z]+)$/i, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase().replace(/\bsar\b/g, "SAR");
}

export function keyLabel(k: string, isAr: boolean): string {
  return isAr ? KEY_AR[k] ?? humanKey(k) : humanKey(k);
}

/** Normalize a URL for display (host + path, no protocol / trailing slash). */
export function shortUrl(u: string): string {
  return String(u).replace(/^https?:\/\//, "").replace(/\/$/, "");
}
